/**
 * Calendar arithmetic for printable calendars and attendance sheets.
 *
 * Everything is the proleptic Gregorian calendar, computed from day counts rather than
 * from `Date` (whose local-time behaviour differs between the server and the browser):
 * `weekday` is Sakamoto's method, `isoWeek` follows ISO 8601 (weeks start on Monday and
 * week 1 holds the year's first Thursday), and `addDays` goes through a civil day number.
 * Months are 0–11 throughout. Names come from `Intl.DateTimeFormat` pinned to UTC so a
 * date never slides across a time-zone boundary.
 */

export interface CivilDate {
  year: number;
  /** 0 = January … 11 = December. */
  month: number;
  day: number;
}

export const MIN_YEAR = 1900;
export const MAX_YEAR = 2200;

export function clampYear(year: number): number {
  if (!Number.isFinite(year)) {
    return MIN_YEAR;
  }
  return Math.min(MAX_YEAR, Math.max(MIN_YEAR, Math.round(year)));
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function daysInMonth(year: number, month: number): number {
  if (month === 1 && isLeapYear(year)) {
    return 29;
  }
  return MONTH_DAYS[month] ?? 30;
}

const SAKAMOTO = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];

/** 0 = Sunday … 6 = Saturday (Sakamoto's algorithm). */
export function weekday(year: number, month: number, day: number): number {
  const y = month < 2 ? year - 1 : year;
  const index =
    (y +
      Math.floor(y / 4) -
      Math.floor(y / 100) +
      Math.floor(y / 400) +
      (SAKAMOTO[month] ?? 0) +
      day) %
    7;
  return (index + 7) % 7;
}

/** Days since 1970-01-01 for a civil date (Hinnant's days_from_civil). */
export function dayNumber(year: number, month: number, day: number): number {
  const m = month + 1;
  const y = m <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (m > 2 ? m - 3 : m + 9) + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146_097 + doe - 719_468;
}

/** The civil date for a day number (Hinnant's civil_from_days). */
export function fromDayNumber(days: number): CivilDate {
  const z = days + 719_468;
  const era = Math.floor(z / 146_097);
  const doe = z - era * 146_097;
  const yoe = Math.floor(
    (doe -
      Math.floor(doe / 1460) +
      Math.floor(doe / 36_524) -
      Math.floor(doe / 146_096)) /
      365
  );
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const month = mp < 10 ? mp + 2 : mp - 10;
  const year = yoe + era * 400 + (month <= 1 ? 1 : 0);
  return { year, month, day };
}

export function addDays(date: CivilDate, count: number): CivilDate {
  return fromDayNumber(dayNumber(date.year, date.month, date.day) + count);
}

/** ISO 8601 week number and week-year. */
export function isoWeek(
  year: number,
  month: number,
  day: number
): { week: number; year: number } {
  const isoDay = (weekday(year, month, day) + 6) % 7; // Monday = 0
  const thursday = fromDayNumber(dayNumber(year, month, day) - isoDay + 3);
  const jan1 = dayNumber(thursday.year, 0, 1);
  const ordinal = dayNumber(thursday.year, thursday.month, thursday.day) - jan1;
  return { week: Math.floor(ordinal / 7) + 1, year: thursday.year };
}

export type WeekStart = 0 | 1;

export interface GridCell extends CivilDate {
  inMonth: boolean;
}

/** The weeks of a month, each a row of seven cells starting on `weekStart`. */
export function monthGrid(
  year: number,
  month: number,
  weekStart: WeekStart
): GridCell[][] {
  const first = weekday(year, month, 1);
  const lead = (first - weekStart + 7) % 7;
  const total = daysInMonth(year, month);
  const weeks = Math.ceil((lead + total) / 7);
  const startNumber = dayNumber(year, month, 1) - lead;
  const grid: GridCell[][] = [];
  for (let w = 0; w < weeks; w += 1) {
    const row: GridCell[] = [];
    for (let d = 0; d < 7; d += 1) {
      const date = fromDayNumber(startNumber + w * 7 + d);
      row.push({
        ...date,
        inMonth: date.year === year && date.month === month,
      });
    }
    grid.push(row);
  }
  return grid;
}

/** The ISO week number for a grid row: that of the row's Thursday. */
export function rowIsoWeek(row: readonly GridCell[]): number {
  const thursday = row.find(
    (cell) => weekday(cell.year, cell.month, cell.day) === 4
  );
  const cell = thursday ?? row[0];
  return cell ? isoWeek(cell.year, cell.month, cell.day).week : 0;
}

function utcDate(year: number, month: number, day: number): Date {
  const date = new Date(Date.UTC(2000, month, day));
  date.setUTCFullYear(year);
  return date;
}

/** "September" in the locale's language. */
export function monthName(
  locale: string,
  month: number,
  style: "long" | "short" = "long"
): string {
  return new Intl.DateTimeFormat(locale, {
    month: style,
    timeZone: "UTC",
  }).format(utcDate(2024, month, 1));
}

/** Weekday names for a week that starts on `weekStart`. */
export function weekdayNames(
  locale: string,
  weekStart: WeekStart,
  style: "long" | "short" | "narrow" = "short"
): string[] {
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: style,
    timeZone: "UTC",
  });
  // 2023-01-01 was a Sunday.
  return Array.from({ length: 7 }, (_, i) =>
    formatter.format(utcDate(2023, 0, 1 + ((i + weekStart) % 7)))
  );
}

/** A civil date as an ISO string (`2026-09-07`). */
export function isoString(date: CivilDate): string {
  const pad = (value: number, size: number) =>
    String(value).padStart(size, "0");
  return `${pad(date.year, 4)}-${pad(date.month + 1, 2)}-${pad(date.day, 2)}`;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parses `YYYY-MM-DD`; null when it is not a real date in the supported years. */
export function parseIsoDate(text: string): CivilDate | null {
  const match = ISO_DATE.exec(text.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (year < MIN_YEAR || year > MAX_YEAR || month < 0 || month > 11) {
    return null;
  }
  if (day < 1 || day > daysInMonth(year, month)) {
    return null;
  }
  return { year, month, day };
}

/** "Dec 25" (or the locale's equivalent) for a civil date. */
export function shortDate(locale: string, date: CivilDate): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(utcDate(date.year, date.month, date.day));
}
